/**
 * Bloque de MITIGANTES por factor del EBR (Actividad Vulnerable · Acuerdo 115/2026).
 * Los 5 factores son fijos: cada uno lista sus mitigantes con responsable, evidencia y
 * estatus. Guardado por factor → `PUT /api/av/ebr/mitigantes` (deja bitácora).
 */
import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, Save, Loader2, AlertTriangle } from "lucide-react";

import { api, apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const COBERTURA = {
  implementado: { label: "Con mitigante implementado", clase: "bg-emerald-100 text-emerald-800" },
  parcial: { label: "Mitigante en proceso", clase: "bg-amber-100 text-amber-800" },
  sin_mitigante: { label: "Sin mitigante", clase: "bg-red-100 text-red-800" },
};

function FactorMitigantes({ factor, estatus, puedeEditar, onSaved }) {
  const [lista, setLista] = useState(factor.mitigantes || []);
  const [saving, setSaving] = useState(false);
  const cob = COBERTURA[factor.cobertura] || COBERTURA.sin_mitigante;

  const set = (i, campo, val) => setLista((p) => p.map((m, idx) => idx === i ? { ...m, [campo]: val } : m));
  const agregar = (descripcion = "") => setLista((p) => [...p, {
    descripcion, responsable: "", evidencia: "", estatus: "pendiente", fecha_implementacion: "", notas: "",
  }]);
  const quitar = (i) => setLista((p) => p.filter((_, idx) => idx !== i));

  const guardar = async () => {
    if (lista.some((m) => String(m.descripcion || "").trim().length < 5)) {
      toast.error("Cada mitigante necesita una descripción de al menos 5 caracteres.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/av/ebr/mitigantes", { factor: factor.clave, mitigantes: lista });
      toast.success(`Mitigantes del factor ${factor.nombre} guardados.`);
      onSaved(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-md border border-slate-200 p-3" data-testid={`av-mitigantes-factor-${factor.clave}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-900">
            {factor.nombre}
            {factor.ponderacion != null && <span className="ml-2 text-xs font-normal text-slate-500">ponderación {factor.ponderacion} %</span>}
          </p>
          <p className="text-xs text-slate-500">{(factor.subvariables || []).join(" · ")}</p>
        </div>
        <Badge className={cob.clase} data-testid={`av-mitigantes-cobertura-${factor.clave}`}>{cob.label}</Badge>
      </div>

      <div className="mt-3 space-y-2">
        {lista.length === 0 && (
          <p className="flex items-center gap-1.5 text-xs italic text-slate-400">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> Sin mitigantes registrados para este factor.
          </p>
        )}
        {lista.map((m, i) => (
          <div key={m.id || i} className="rounded-md border border-slate-200 bg-slate-50/60 p-2.5"
            data-testid={`av-mitigante-${factor.clave}-${i}`}>
            <div className="flex gap-2">
              <Input className="h-8 flex-1" placeholder="Control implementado para reducir el riesgo"
                data-testid={`av-mitigante-desc-${factor.clave}-${i}`} value={m.descripcion} disabled={!puedeEditar}
                onChange={(e) => set(i, "descripcion", e.target.value)} />
              {puedeEditar && (
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => quitar(i)}
                  data-testid={`av-mitigante-quitar-${factor.clave}-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-4">
              <Input className="h-8" placeholder="Responsable" value={m.responsable} disabled={!puedeEditar}
                data-testid={`av-mitigante-resp-${factor.clave}-${i}`}
                onChange={(e) => set(i, "responsable", e.target.value)} />
              <Input className="h-8" placeholder="Evidencia (documento, política, sistema)" value={m.evidencia}
                disabled={!puedeEditar} data-testid={`av-mitigante-evid-${factor.clave}-${i}`}
                onChange={(e) => set(i, "evidencia", e.target.value)} />
              <select className="h-8 rounded border border-slate-300 px-2 text-sm disabled:bg-slate-100"
                data-testid={`av-mitigante-estatus-${factor.clave}-${i}`} value={m.estatus} disabled={!puedeEditar}
                onChange={(e) => set(i, "estatus", e.target.value)}>
                {estatus.map((s) => <option key={s.clave} value={s.clave}>{s.label}</option>)}
              </select>
              <Input className="h-8" type="date" value={m.fecha_implementacion || ""} disabled={!puedeEditar}
                data-testid={`av-mitigante-fecha-${factor.clave}-${i}`}
                onChange={(e) => set(i, "fecha_implementacion", e.target.value)} />
            </div>
          </div>
        ))}
      </div>

      {puedeEditar && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => agregar()} data-testid={`av-mitigante-agregar-${factor.clave}`}>
            <Plus className="mr-1.5 h-4 w-4" /> Agregar mitigante
          </Button>
          <Button size="sm" onClick={guardar} disabled={saving} className="bg-primary hover:bg-sidebar-hover"
            data-testid={`av-mitigante-guardar-${factor.clave}`}>
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Guardar factor
          </Button>
          {(factor.sugeridos || []).length > 0 && (
            <select className="h-8 max-w-full rounded border border-slate-300 px-2 text-sm"
              data-testid={`av-mitigante-sugerido-${factor.clave}`} value=""
              onChange={(e) => { if (e.target.value) agregar(e.target.value); }}>
              <option value="">Usar control sugerido…</option>
              {factor.sugeridos.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </div>
      )}
    </div>
  );
}

export function AvEbrMitigantes({ factores, estatus, puedeEditar, onSaved }) {
  return (
    <Card data-testid="av-ebr-mitigantes">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-4 w-4 text-emerald-700" /> Mitigantes por factor de riesgo
        </CardTitle>
        <p className="text-xs text-slate-500">
          Los 5 factores son fijos. Registra por cada uno el control que reduce el riesgo, con
          responsable, evidencia y estatus. Cada guardado queda en la bitácora.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {(factores || []).map((f) => (
          <FactorMitigantes key={f.clave} factor={f} estatus={estatus}
            puedeEditar={puedeEditar} onSaved={onSaved} />
        ))}
      </CardContent>
    </Card>
  );
}
